import React, { useState } from 'react';
import { X, Plus, MapPin, Package } from 'lucide-react';

const QuickAddEquipmentModal = ({ isOpen, onClose, onSave, equipmentType = '' }) => {
  const [equipment, setEquipment] = useState({
    type: equipmentType || '',
    category: 'FFE',
    location: ''
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!equipment.type || !equipment.location) {
      alert('Please enter equipment type and location');
      return;
    }

    // Generate ID for equipment family
    const id = `${equipment.type}_${equipment.location.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;

    onSave({
      id,
      type: equipment.type,
      category: equipment.category,
      location: equipment.location,
      source: 'manual',
      dateAdded: new Date().toISOString(),
      units: [] // Start with no units - user will add them via "+ Add unit" button
    });

    // Reset form
    setEquipment({
      type: equipmentType || '',
      category: 'FFE',
      location: ''
    });

    onClose();
  };

  const categories = [
    { value: 'FFE', label: 'Fire Fighting Equipment' },
    { value: 'LSA', label: 'Life Saving Appliances' },
    { value: 'Navigation', label: 'Navigation Equipment' },
    { value: 'Galley', label: 'Galley Equipment' },
    { value: 'Machinery', label: 'Machinery' },
    { value: 'Deck', label: 'Deck Equipment' },
    { value: 'Safety', label: 'Safety Equipment' },
    { value: 'Other', label: 'Other' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Add Equipment to Register
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Create equipment family - add individual units after creation
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              {/* Equipment Type */}
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-900">
                  Equipment Type <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="type"
                    type="text"
                    value={equipment.type}
                    onChange={(e) => setEquipment({ ...equipment, type: e.target.value })}
                    placeholder="e.g., Portable Extinguisher - Control Position"
                    required
                    disabled={!!equipmentType}
                    autoComplete="off"
                    className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  The name/description of this equipment type
                </p>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-900">
                  <Package className="inline h-4 w-4 mr-1" />
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <select
                    id="category"
                    value={equipment.category}
                    onChange={(e) => setEquipment({ ...equipment, category: e.target.value })}
                    required
                    className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Equipment category for organization
                </p>
              </div>

              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-900">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Location <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="location"
                    type="text"
                    value={equipment.location}
                    onChange={(e) => setEquipment({ ...equipment, location: e.target.value })}
                    placeholder="e.g., Control position, Bridge, Engine Room"
                    required
                    autoComplete="off"
                    className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Where this equipment is located on the vessel
                </p>
              </div>

              {/* Info Box */}
              <div className="rounded-md bg-blue-50 p-4 mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Package className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Adding Individual Units
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        After creating this equipment family, you can add individual units with serial numbers,
                        service dates, and other specific details using the "+ Add unit" button.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700"
                >
                  <Plus className="inline h-4 w-4 mr-1" />
                  Add Equipment
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickAddEquipmentModal;
